import React from 'react';
import { Check, Flame, Calculator } from 'lucide-react';
import Reveal from './Reveal';

const RING_COLORS = ['#D4F63F', '#8B7CFF', '#5EC8F2'];

function MacroRing({ label, pct, color, dailyGoal }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="relative w-28 h-28 rounded-full"
        style={{
          background: `conic-gradient(${color} ${pct * 3.6}deg, rgba(148,163,184,0.12) 0deg)`,
        }}
      >
        <div className="absolute inset-[10px] rounded-full bg-surface flex flex-col items-center justify-center">
          <span className="font-display text-xl font-bold text-frost">{pct}%</span>
        </div>
      </div>
      <span className="text-xs font-semibold text-mist uppercase tracking-widest">{label}</span>
    </div>
  );
}

export default function Nutrition({ t }) {
  const n = t.nutrition;

  return (
    <section id="ernaehrung" className="relative bg-ink py-24 lg:py-32 overflow-hidden">
      <div className="absolute top-1/4 -left-32 w-[420px] h-[420px] rounded-full bg-volt-400/8 blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          {/* Left: copy + features */}
          <Reveal>
            <div className="flex flex-col gap-6">
              <span className="inline-flex items-center gap-2 w-fit px-4 py-1.5 rounded-full border border-volt-400/40 bg-volt-400/10 text-volt-400 text-xs font-bold uppercase tracking-[0.18em]">
                <Flame size={14} />
                {n.kicker}
              </span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-frost tracking-tight leading-tight">
                {n.title}
              </h2>
              <p className="text-lg text-mist leading-relaxed">{n.sub}</p>

              <ul className="flex flex-col gap-3 pt-2">
                {n.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-frost/90">
                    <span className="w-6 h-6 rounded-full bg-volt-400/15 border border-volt-400/40 flex items-center justify-center shrink-0">
                      <Check size={13} className="text-volt-400" strokeWidth={3} />
                    </span>
                    <span className="font-medium">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* Right: macro rings + calculator */}
          <Reveal delay={120}>
            <div className="flex flex-col gap-6">
              {/* Macro card */}
              <div className="rounded-3xl border border-line bg-surface p-8 shadow-card">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-mist mb-6">
                  {n.dailyGoal}
                </p>
                <div className="flex items-center justify-around flex-wrap gap-6">
                  {n.macros.map((m, i) => (
                    <MacroRing key={m.label} label={m.label} pct={m.pct} color={RING_COLORS[i]} />
                  ))}
                </div>
              </div>

              {/* Calculator card */}
              <div className="rounded-3xl border border-pulse-400/30 bg-gradient-to-br from-surface to-abyss p-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-pulse-500/20 border border-pulse-400/40 flex items-center justify-center">
                    <Calculator size={18} className="text-pulse-400" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-frost">{n.calculatorTitle}</h3>
                </div>
                <p className="text-sm text-mist mb-5">{n.calculatorSub}</p>
                <div className="flex flex-wrap gap-2.5">
                  {n.calculatorInputs.map((input) => (
                    <span
                      key={input}
                      className="px-4 py-2 rounded-full border border-line bg-ink/60 text-sm font-medium text-frost/85 hover:border-pulse-400/50 hover:text-pulse-400 transition-colors duration-200 cursor-default"
                    >
                      {input}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
